import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import formatUuidT from "../flatbuffers/display/uuid";

/**
 * Returns the Set of bank UUIDs currently selected by the monitoring dashboard's
 * cascader, or `null` when nothing is selected.
 *
 * The monitoring cascader writes its selection to
 * `state.organization.organizationSelectMultipleData` as cascader bank items.
 * Each item carries `idBank` (cascader shape) or `id` (favorite-grid shape).
 *
 * Callers should treat `null` as "do not filter" so users who haven't picked
 * anything on monitoring still see the full org-scoped list in report filters.
 */
export default function useMonitoringSelectedBankIds(): Set<string> | null {
  const selectedBanks = useSelector(
    (state: RootState) => state.organization.organizationSelectMultipleData,
  );

  return useMemo(() => {
    if (!selectedBanks || selectedBanks.length === 0) return null;
    const ids = new Set<string>();
    selectedBanks.forEach((bank: any) => {
      const raw = bank?.idBank ?? bank?.id;
      const normalized = formatUuidT(raw);
      if (normalized) ids.add(normalized);
    });
    return ids.size > 0 ? ids : null;
  }, [selectedBanks]);
}
